/**
 * @splitfiesta/i18n/web
 *
 * Composes the SimpleI18n singleton with web-specific language detection
 * (navigator.language) and persistence (localStorage), then re-exports
 * the full core surface.
 *
 * Web consumers import from this entry: `import { useTranslation } from
 * '@splitfiesta/i18n/web'`. The mobile entry (`@splitfiesta/i18n/mobile`)
 * provides the equivalent composition for React Native.
 *
 * IMPORTANT: This module evaluates exactly once per app process —
 * importing it constructs the singleton. Don't import it from code paths
 * that are also evaluated by the mobile bundle.
 */
import {
  DEFAULT_LANGUAGE,
  type SupportedLanguage,
  getNearestSupportedLanguage,
  isLanguageSupported,
} from '../config';
import { SimpleI18n, setActiveI18nInstance } from '../core';

const STORAGE_KEY = 'language';

function detectInitialLanguage(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isLanguageSupported(saved as SupportedLanguage)) {
      return saved;
    }
    const detected = getNearestSupportedLanguage(navigator.language);
    localStorage.setItem(STORAGE_KEY, detected);
    return detected;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

function persistLanguage(language: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    /* localStorage may be unavailable (private mode, ITP) — best-effort */
  }
}

export const i18n = new SimpleI18n({
  initialLanguage: detectInitialLanguage(),
  persist: persistLanguage,
});

setActiveI18nInstance(i18n);

// Re-export the entire core surface so consumers only need this one entry.
export * from '../core';
